import type { Attachment, Message, Participant } from "@catmail/shared";
import { AlertCircle, Cat, Clock3, FileCode2, RotateCcw } from "lucide-react";

import { Avatar } from "@/components";
import { cn } from "@/lib/cn";
import { FOCUS, INTERACTIVE, PRESS_FLAT } from "@/lib/interaction";
import { BUBBLE_IN, ENTER } from "@/lib/motion";
import { formatTime } from "@/lib/datetime";

import { avatarToneOf, filesOf } from "../../lib/roomView";
import { AttachmentCard } from "../AttachmentCard";

type MessageBubbleProps = {
  message: Message;
  sender: Participant | null;
  /** 같은 사람이 연달아 보낸 두 번째 이후 — 아바타와 이름을 지우고 꼬리도 뗀다 */
  continuation: boolean;
  /** 그룹방에서만 보낸사람 이름을 띄운다 */
  showSenderName: boolean;
  /** 조용한 방은 글자를 흐리게 — 안 봐도 되는 것처럼 보이도록 */
  quiet: boolean;
  onOpenHtml?: (message: Message) => void;
  onOpenAttachment?: (attachment: Attachment) => void;
  /** 아바타나 이름을 눌렀을 때. 없으면 눌리지 않는다(내 말풍선). */
  onOpenProfile?: (sender: Participant) => void;
  /** 못 보낸 메일을 다시 큐에 넣는다 */
  onRetry?: (messageId: string) => void;
  /**
   * 방을 열어둔 채로 방금 도착한 것인가.
   *
   * 열 때 이미 있던 말풍선까지 떠오르면 대화를 읽는 게 아니라 화면이 요동치는
   * 것으로 보인다. 판단은 타임라인이 한다.
   */
  entering?: boolean;
};

export function MessageBubble({
  message,
  sender,
  continuation,
  showSenderName,
  quiet,
  onOpenHtml,
  onOpenAttachment,
  onOpenProfile,
  onRetry,
  entering = false,
}: MessageBubbleProps) {
  /*
   * 말풍선 폭에 절대 상한을 둔다.
   *
   * 비율만 두면 대화창을 넓힐수록(사이드바를 좁히면 그렇게 된다) 한 줄이 계속
   * 길어진다. 메일 본문은 채팅 한 마디보다 길어서 금세 한 줄 100자를 넘고,
   * 그러면 눈이 다음 줄 첫 글자를 못 찾는다.
   */
  const outgoing = message.direction === "outgoing";
  const failed = message.delivery === "failed";
  // 본문에 박힌 그림은 첨부 카드로 세지 않는다. 원문 화면이 본문 안에 그린다.
  const files = filesOf(message);
  /**
   * 아직 실제로 나가지 않았다. 발송은 15초마다 도는 큐가 하므로 잠깐 이 상태로
   * 머무는 게 정상이다. 시각을 미리 박아두면 보낸 적 없는 시각을 보여주게 된다.
   */
  const pending =
    outgoing && (message.delivery === "queued" || message.delivery === "sending");

  /**
   * 같은 Cat Mail 서버 사람이 보낸 말인가.
   *
   * 바깥 메일과 겉모습이 똑같으면 **읽음이 왜 이 방에만 뜨는지**, 아바타 점이
   * 왜 여기서만 진짜 접속인지 알 길이 없다. 그 차이를 만드는 사실을 말풍선
   * 옆에 한 번 적어둔다. 보낸 사람 기준이라 내 말풍선에는 붙이지 않는다 —
   * 내 주소는 늘 이 서버라 모든 말풍선에 붙으면 아무것도 구별하지 못한다.
   */
  const fromCatmail = !outgoing && sender?.internal === true;

  const meta = (
    <span className="flex shrink-0 flex-col items-end gap-0.5 text-[11px] whitespace-nowrap text-muted-3">
      {fromCatmail ? (
        <Cat className="size-3.5 text-muted-2" aria-label="Cat Mail 사용자" />
      ) : null}
      {/* 카카오톡처럼 **사람 수**로 읽는다. "읽음 1"은 무엇이 1인지 말해주지 않는다. */}
      {outgoing && message.readBy > 0 ? <span>{message.readBy}명 읽음</span> : null}
      {pending ? (
        <Clock3 className="size-3" aria-label="보내는 중" />
      ) : (
        <span>{formatTime(message.sentAt)}</span>
      )}
    </span>
  );

  const bubble = (
    <div
      className={cn(
        // 메일 본문은 줄바꿈이 의미를 갖는다. pre-wrap이 없으면 여러 줄짜리
        // 메일이 통째로 한 줄로 붙어버린다. break-words가 없으면 긴 URL이
        // 말풍선을 뚫고 나간다.
        "px-4 py-3 text-[15px] leading-[1.55] break-words whitespace-pre-wrap",
        outgoing
          ? "bg-ink text-on-ink"
          : cn(
              "border border-line-bubble bg-surface",
              quiet ? "text-body" : "text-ink",
            ),
        continuation
          ? "rounded-bubble"
          : outgoing
            ? "rounded-bubble-out"
            : "rounded-bubble-in",
      )}
    >
      {/* 조용한 방은 광고·안내라 제목이 곧 본론이다. 제목을 첫 줄로 올린다. */}
      {quiet && !continuation ? (
        <div className="flex flex-col gap-1.5">
          <span>{message.subject}</span>
          <span className="text-[13px] text-muted-2">{message.body}</span>
        </div>
      ) : (
        message.body
      )}
    </div>
  );

  if (outgoing) {
    return (
      <div className={cn("flex flex-col items-end gap-1.25", entering && BUBBLE_IN)}>
        <div className="flex max-w-[min(82%,36rem)] sm:max-w-[min(62%,36rem)] items-end gap-1.75">
          {meta}
          {bubble}
        </div>

        {/*
          실패를 알리는 것만으로는 부족하다. 여기서 다시 보낼 수 없으면 그 말은
          대화 안에 영영 빨간 줄로 남는다 — 지울 수도, 보낼 수도 없이.
        */}
        {failed ? (
          <span
            className={`flex items-center gap-1.5 text-[11px] text-danger ${ENTER}`}
          >
            <AlertCircle className="size-3 shrink-0" />
            <span>{message.error ?? "보내지 못했어요"}</span>
            {onRetry ? (
              <button
                type="button"
                onClick={() => onRetry(message.id)}
                className={`flex shrink-0 items-center gap-0.5 rounded border-b border-danger/40 font-semibold hover:border-danger ${PRESS_FLAT} ${FOCUS}`}
              >
                <RotateCcw className="size-2.5" />
                다시 보내기
              </button>
            ) : null}
          </span>
        ) : null}

        {files.map((attachment) => (
          <AttachmentCard
            key={attachment.id}
            attachment={attachment}
            onOpen={onOpenAttachment}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("flex max-w-[min(82%,36rem)] sm:max-w-[min(62%,36rem)] gap-2.5", entering && BUBBLE_IN)}>
      {continuation || !sender ? (
        <div className="size-9 shrink-0" />
      ) : (
        // 아바타를 누르면 프로필이 뜬다. 헤더의 아바타가 그렇게 동작하는데
        // 여기만 안 되면, 같게 생긴 것 하나만 반응하지 않는 셈이라 고장으로 읽힌다.
        <button
          type="button"
          aria-label={`${sender.displayName} 프로필`}
          onClick={() => onOpenProfile?.(sender)}
          disabled={!onOpenProfile}
          className={`shrink-0 rounded-avatar-sm disabled:cursor-default ${INTERACTIVE}`}
        >
          <Avatar
            size="md"
            initials={sender.initials}
            colorIndex={sender.colorIndex}
            imageUrl={sender.avatarUrl}
            dotTone={avatarToneOf(sender)}
            label={sender.displayName}
          />
        </button>
      )}

      <div className="flex min-w-0 flex-col gap-1.25">
        {showSenderName && !continuation && sender ? (
          <button
            type="button"
            onClick={() => onOpenProfile?.(sender)}
            disabled={!onOpenProfile}
            className={`self-start rounded text-[13px] text-muted hover:text-body disabled:cursor-default ${PRESS_FLAT} ${FOCUS}`}
          >
            {sender.displayName}
          </button>
        ) : null}

        <div className="flex items-end gap-1.75">
          {bubble}
          {meta}
        </div>

        {message.hasHtml ? (
          <button
            type="button"
            onClick={() => onOpenHtml?.(message)}
            className={`-mx-1 flex items-center gap-1.5 self-start rounded px-1 py-0.5 text-xs text-muted hover:text-ink active:text-body ${PRESS_FLAT} ${FOCUS}`}
          >
            <FileCode2 className="size-3.5" />
            메일 원본 보기
          </button>
        ) : null}

        {files.map((attachment) => (
          <AttachmentCard
            key={attachment.id}
            attachment={attachment}
            onOpen={onOpenAttachment}
          />
        ))}
      </div>
    </div>
  );
}
